// frontend/src/pages/catalog/CheckoutSessionsPage.tsx
//
// 收银会话页：会话列表 + 创建收银会话 Modal（级联选价格或临时金额）
// + 创建成功后展示可复制的托管收银台 URL。
// 后端：/merchant/checkout-sessions（创建响应 data={session,url,session_token}）。

import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Cascader,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Result,
  Select,
  Space,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  CopyOutlined,
  LinkOutlined,
  PlusOutlined,
  ReloadOutlined,
  ShoppingOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import CatalogService, {
  CheckoutMode,
  CheckoutSession,
  CheckoutSessionResult,
  CreateCheckoutSessionRequest,
  ListMeta,
  Price,
  Product,
} from '../../services/catalog';
import { formatAmount, yuanToCents } from '../../utils/money';
import { recurringText } from './ProductsPage';

const { Title, Text, Paragraph } = Typography;

const MODE_LABELS: Record<string, { label: string; color: string }> = {
  payment: { label: '单次支付', color: 'blue' },
  subscription: { label: '订阅', color: 'purple' },
  setup: { label: '绑卡', color: 'cyan' },
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  open: { label: '待支付', color: 'processing' },
  complete: { label: '已完成', color: 'success' },
  expired: { label: '已过期', color: 'default' },
  canceled: { label: '已取消', color: 'error' },
};

const CURRENCY_OPTIONS = ['USD', 'EUR', 'GBP', 'CNY', 'JPY', 'MYR', 'SGD', 'HKD'].map((c) => ({
  value: c,
  label: c,
}));

interface CascaderOption {
  value: string;
  label: React.ReactNode;
  children?: CascaderOption[];
  disabled?: boolean;
}

/** 用本前端域名重建托管收银台 URL（列表行场景，创建结果优先用后端返回的 url） */
const buildSessionUrl = (session: CheckoutSession): string | null => {
  if (!session.session_token) return null;
  return `${window.location.origin}/checkout/${session.id}?token=${session.session_token}`;
};

const CheckoutSessionsPage: React.FC = () => {
  const [sessions, setSessions] = useState<CheckoutSession[]>([]);
  const [meta, setMeta] = useState<ListMeta>({
    total: 0,
    per_page: 20,
    current_page: 1,
    last_page: 1,
  });
  const [loading, setLoading] = useState(false);

  // 创建 Modal
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form] = Form.useForm();
  const modeValue: CheckoutMode = Form.useWatch('mode', form) || 'payment';
  const itemSource: 'price' | 'ad_hoc' = Form.useWatch('item_source', form) || 'price';

  // 商品→价格级联
  const [cascaderOptions, setCascaderOptions] = useState<CascaderOption[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  // 创建成功结果
  const [createdResult, setCreatedResult] = useState<CheckoutSessionResult | null>(null);

  const loadSessions = useCallback(async (page: number = 1, perPage: number = 20) => {
    setLoading(true);
    try {
      const result = await CatalogService.listCheckoutSessions(page, perPage);
      setSessions(result.data);
      setMeta(result.meta);
    } catch (err: any) {
      message.error(err.response?.data?.message || '获取收银会话列表失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  // 加载商品 + 价格，构建级联选项
  const loadCatalog = useCallback(async () => {
    setCatalogLoading(true);
    try {
      const [productResult, priceResult] = await Promise.all([
        CatalogService.listProducts(1, 100, true),
        CatalogService.listPrices(undefined, 1, 200, true),
      ]);
      const pricesByProduct: Record<string, Price[]> = {};
      priceResult.data.forEach((p) => {
        if (!pricesByProduct[p.product_id]) pricesByProduct[p.product_id] = [];
        pricesByProduct[p.product_id].push(p);
      });
      const options: CascaderOption[] = productResult.data.map((product: Product) => {
        const prices = pricesByProduct[product.id] || [];
        return {
          value: product.id,
          label: product.name,
          disabled: prices.length === 0,
          children: prices.map((price) => ({
            value: price.id,
            label: `${price.nickname ? `${price.nickname} · ` : ''}${formatAmount(
              price.unit_amount,
              price.currency
            )} / ${recurringText(price)}`,
          })),
        };
      });
      setCascaderOptions(options);
    } catch (err: any) {
      message.error(err.response?.data?.message || '加载商品价格失败');
    } finally {
      setCatalogLoading(false);
    }
  }, []);

  const openCreate = () => {
    form.resetFields();
    form.setFieldsValue({
      mode: 'payment',
      presentation: 'link',
      item_source: 'price',
      quantity: 1,
      ad_hoc_currency: 'USD',
      success_url: `${window.location.origin}/checkout/success`,
      cancel_url: `${window.location.origin}/checkout/cancel`,
    });
    setCreateOpen(true);
    if (cascaderOptions.length === 0) {
      loadCatalog();
    }
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      setCreating(true);

      const payload: CreateCheckoutSessionRequest = {
        mode: values.mode,
        presentation: values.presentation,
        line_items: [],
        success_url: values.success_url,
        cancel_url: values.cancel_url,
      };
      if (values.customer_email) payload.customer_email = values.customer_email;
      if (values.buyer_country) payload.buyer_country = values.buyer_country.toUpperCase();

      if (values.mode !== 'setup') {
        if (values.item_source === 'price') {
          const cascade: string[] = values.price_cascade || [];
          payload.line_items = [
            {
              price_id: cascade[1],
              quantity: values.quantity || 1,
            },
          ];
        } else {
          payload.line_items = [
            {
              quantity: values.quantity || 1,
              ad_hoc_amount: yuanToCents(values.ad_hoc_amount),
              ad_hoc_currency: values.ad_hoc_currency,
              description: values.ad_hoc_description,
            },
          ];
        }
      }

      const result = await CatalogService.createCheckoutSession(payload);
      message.success('收银会话已创建');
      setCreateOpen(false);
      setCreatedResult(result);
      loadSessions(1, meta.per_page);
    } catch (err: any) {
      if (err.errorFields) return;
      message.error(err.response?.data?.message || '创建收银会话失败');
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    message.success('已复制到剪贴板');
  };

  // ---- 表格列 ----

  const columns: ColumnsType<CheckoutSession> = [
    {
      title: '会话 ID',
      dataIndex: 'id',
      key: 'id',
      width: 240,
      render: (id: string) => (
        <Text copyable style={{ fontSize: 12 }}>
          {id}
        </Text>
      ),
    },
    {
      title: '模式',
      dataIndex: 'mode',
      key: 'mode',
      width: 100,
      render: (mode: string) => {
        const conf = MODE_LABELS[mode] || { label: mode, color: 'default' };
        return <Tag color={conf.color}>{conf.label}</Tag>;
      },
    },
    {
      title: '金额',
      dataIndex: 'amount_total',
      key: 'amount_total',
      width: 130,
      render: (amount: number, record) =>
        record.mode === 'setup' && !amount ? (
          <Text type="secondary">-</Text>
        ) : (
          <Text strong>{formatAmount(amount, record.currency)}</Text>
        ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => {
        const conf = STATUS_LABELS[status] || { label: status, color: 'default' };
        return <Tag color={conf.color}>{conf.label}</Tag>;
      },
    },
    {
      title: '客户邮箱',
      dataIndex: 'customer_email',
      key: 'customer_email',
      render: (email: string) => email || <Text type="secondary">-</Text>,
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (t: string) => (t ? dayjs(t).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 170,
      render: (_, record) => {
        const url = record.status === 'open' ? buildSessionUrl(record) : null;
        if (!url) return <Text type="secondary">-</Text>;
        return (
          <Space size="small">
            <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => handleCopy(url)}>
              复制链接
            </Button>
            <Button
              type="link"
              size="small"
              icon={<LinkOutlined />}
              onClick={() => window.open(url, '_blank')}
            >
              打开
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      <Card>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            <ShoppingOutlined /> 收银会话
          </Title>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => loadSessions(meta.current_page, meta.per_page)}
            >
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              创建收银会话
            </Button>
          </Space>
        </div>

        <Table<CheckoutSession>
          rowKey="id"
          columns={columns}
          dataSource={sessions || []}
          loading={loading}
          locale={{ emptyText: '暂无数据' }}
          scroll={{ x: 1100 }}
          pagination={{
            current: meta.current_page,
            pageSize: meta.per_page,
            total: meta.total,
            showSizeChanger: true,
            showTotal: (total) => `共 ${total} 条`,
            onChange: (page, pageSize) => loadSessions(page, pageSize),
          }}
        />
      </Card>

      {/* 创建收银会话 */}
      <Modal
        title="创建收银会话"
        open={createOpen}
        onOk={handleCreate}
        onCancel={() => setCreateOpen(false)}
        confirmLoading={creating}
        okText="创建"
        cancelText="取消"
        width={620}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item label="模式" name="mode" rules={[{ required: true, message: '请选择模式' }]}>
            <Radio.Group>
              <Radio.Button value="payment">单次支付</Radio.Button>
              <Radio.Button value="subscription">订阅</Radio.Button>
              <Radio.Button value="setup">绑定支付方式</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {modeValue !== 'setup' && (
            <>
              <Form.Item label="商品来源" name="item_source">
                <Radio.Group>
                  <Radio value="price">商品目录价格</Radio>
                  <Radio value="ad_hoc" disabled={modeValue === 'subscription'}>
                    临时金额（仅 PSP 模式）
                  </Radio>
                </Radio.Group>
              </Form.Item>

              {itemSource === 'price' || modeValue === 'subscription' ? (
                <Form.Item
                  label="选择价格（商品 → 价格）"
                  name="price_cascade"
                  rules={[{ required: true, message: '请选择商品价格' }]}
                >
                  <Cascader
                    options={cascaderOptions}
                    loading={catalogLoading}
                    placeholder="请选择商品 → 价格"
                    showSearch
                    expandTrigger="hover"
                  />
                </Form.Item>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: 12 }}>
                    <Form.Item
                      label="临时金额（元）"
                      name="ad_hoc_amount"
                      style={{ flex: 1 }}
                      rules={[{ required: true, message: '请输入金额' }]}
                    >
                      <InputNumber
                        style={{ width: '100%' }}
                        min={0.01}
                        precision={2}
                        placeholder="例如 19.99"
                      />
                    </Form.Item>
                    <Form.Item
                      label="币种"
                      name="ad_hoc_currency"
                      style={{ width: 140 }}
                      rules={[{ required: true, message: '请选择币种' }]}
                    >
                      <Select options={CURRENCY_OPTIONS} />
                    </Form.Item>
                  </div>
                  <Form.Item label="商品描述" name="ad_hoc_description">
                    <Input placeholder="展示在收银台的商品描述（可选）" maxLength={255} />
                  </Form.Item>
                </>
              )}

              <Form.Item label="数量" name="quantity">
                <InputNumber style={{ width: 160 }} min={1} precision={0} />
              </Form.Item>
            </>
          )}

          {modeValue === 'setup' && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="绑卡模式不收款，买家在收银台完成支付方式绑定。"
            />
          )}

          <Form.Item
            label="客户邮箱（可选）"
            name="customer_email"
            rules={[{ type: 'email', message: '邮箱格式不正确' }]}
          >
            <Input placeholder="customer@example.com" />
          </Form.Item>

          <Form.Item
            label="成功跳转 URL"
            name="success_url"
            rules={[
              { required: true, message: '请输入成功跳转 URL' },
              { type: 'url', message: 'URL 格式不正确' },
            ]}
          >
            <Input placeholder="https://example.com/success" />
          </Form.Item>
          <Form.Item
            label="取消跳转 URL"
            name="cancel_url"
            rules={[
              { required: true, message: '请输入取消跳转 URL' },
              { type: 'url', message: 'URL 格式不正确' },
            ]}
          >
            <Input placeholder="https://example.com/cancel" />
          </Form.Item>

          <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item label="呈现方式" name="presentation" style={{ flex: 1 }}>
              <Radio.Group>
                <Radio.Button value="link">链接跳转</Radio.Button>
                <Radio.Button value="iframe">iframe 弹层</Radio.Button>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              label="买家国家（可选，ISO 两位码）"
              name="buyer_country"
              style={{ width: 220 }}
            >
              <Input placeholder="例如 US" maxLength={2} />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      {/* 创建成功：展示托管 URL */}
      <Modal
        open={!!createdResult}
        footer={null}
        onCancel={() => setCreatedResult(null)}
        width={620}
        destroyOnClose
      >
        {createdResult && (
          <Result
            status="success"
            title="收银会话已创建"
            subTitle={`会话 ID：${createdResult.session?.id || '-'}`}
            extra={
              <div style={{ textAlign: 'left' }}>
                <Paragraph>
                  <Text type="secondary">托管收银台 URL：</Text>
                </Paragraph>
                <Paragraph
                  copyable={{ text: createdResult.url }}
                  style={{
                    background: '#f5f7fa',
                    padding: '8px 12px',
                    borderRadius: 6,
                    wordBreak: 'break-all',
                  }}
                >
                  {createdResult.url}
                </Paragraph>
                <Space style={{ marginTop: 8 }}>
                  <Button
                    type="primary"
                    icon={<LinkOutlined />}
                    onClick={() => window.open(createdResult.url, '_blank')}
                  >
                    打开收银台
                  </Button>
                  <Button icon={<CopyOutlined />} onClick={() => handleCopy(createdResult.url)}>
                    复制链接
                  </Button>
                </Space>
              </div>
            }
          />
        )}
      </Modal>
    </div>
  );
};

export default CheckoutSessionsPage;
